//TODO : Refactor after ai
/**
 * Fachliche Abläufe des Prototyps – was zwischen den Objekten passiert,
 * wenn jemand eine Action auslöst. Alles rein: Jede Funktion bekommt den
 * Bestand und liefert eine Änderung zurück, die der Store anwendet.
 *
 * Die Rechenregeln (Ansätze, Leistungswerte, Kostensätze) sind Annahmen,
 * solange Viacid die Kalkulationsparameter nicht geliefert hat.
 */

import type { Datenbestand } from "./index";
import type { Datensatz, Wert } from "./types";

export interface NeuerSatz {
  slug: string;
  satz: Datensatz;
}

export interface SatzAenderung {
  slug: string;
  id: string;
  status?: string;
  werte?: Record<string, Wert>;
  bezuege?: Record<string, string | string[]>;
}

export interface Aenderung {
  neu: NeuerSatz[];
  geaendert: SatzAenderung[];
  meldung: string;
  /** Der Datensatz, der danach geöffnet werden soll. */
  oeffnen?: { slug: string; id: string };
  fehler?: boolean;
}

export const fehlerMeldung = (meldung: string): Aenderung => ({
  neu: [],
  geaendert: [],
  meldung,
  fehler: true,
});

export const heute = (): string => new Date().toISOString().slice(0, 10);
const jetzt = (): string => new Date().toISOString().slice(0, 16);

const zahl = (w: Wert | undefined): number =>
  typeof w === "number" ? w : Number(w ?? 0) || 0;
const runde = (n: number, stellen = 2): number =>
  Math.round(n * 10 ** stellen) / 10 ** stellen;
const auf5Rappen = (n: number): number => Math.round(n * 20) / 20;
const ein = (w: string | string[] | undefined): string | undefined =>
  Array.isArray(w) ? w[0] : w;
const liste = (w: string | string[] | undefined): string[] =>
  w == null ? [] : Array.isArray(w) ? w : [w];

const saetze = (b: Datenbestand, slug: string): Datensatz[] => b[slug] ?? [];
const satz = (
  b: Datenbestand,
  slug: string,
  id: string | undefined
): Datensatz | undefined =>
  id ? saetze(b, slug).find((d) => d.id === id) : undefined;
const mit = (
  b: Datenbestand,
  slug: string,
  rel: string,
  id: string
): Datensatz[] =>
  saetze(b, slug).filter((d) => liste(d.bezuege[rel]).includes(id));

/** Eindeutige ID im Prototyp. */
export function neueId(
  b: Datenbestand,
  slug: string,
  schon: NeuerSatz[] = []
): string {
  const n =
    saetze(b, slug).length + schon.filter((s) => s.slug === slug).length + 1;
  return `${slug}-neu-${n}-${Date.now().toString(36).slice(-4)}`;
}

/** Nächste Nummer im Muster der vorhandenen (AN-2026-0412 → AN-2026-0413). */
export function naechsteKennung(
  b: Datenbestand,
  slug: string,
  schon: NeuerSatz[] = []
): string | null {
  const alle = [
    ...saetze(b, slug),
    ...schon.filter((s) => s.slug === slug).map((s) => s.satz),
  ]
    .map((d) => d.kennung)
    .filter((k): k is string => k != null);
  const muster = alle
    .map((k) => /^(.*?)(\d+)$/.exec(k))
    .filter((m): m is RegExpExecArray => m != null);
  if (muster.length === 0) return null;
  const hoechste = muster.reduce((a, m) =>
    Number(m[2]) > Number(a[2]) ? m : a
  );
  return `${hoechste[1]}${String(Number(hoechste[2]) + 1).padStart(hoechste[2].length, "0")}`;
}

function neu(
  b: Datenbestand,
  slug: string,
  schon: NeuerSatz[],
  felder: {
    titel: string;
    untertitel?: string;
    status?: string;
    kennungFeld?: string;
    werte: Record<string, Wert>;
  },
  bezuege: Record<string, string | string[] | undefined>
): NeuerSatz {
  const kennung = felder.kennungFeld ? naechsteKennung(b, slug, schon) : null;
  const werte = { ...felder.werte };
  if (felder.kennungFeld && kennung) werte[felder.kennungFeld] = kennung;
  const bz: Record<string, string | string[]> = {};
  for (const [k, v] of Object.entries(bezuege))
    if (v != null && !(Array.isArray(v) && v.length === 0)) bz[k] = v;
  return {
    slug,
    satz: {
      id: neueId(b, slug, schon),
      titel: felder.titel,
      untertitel: felder.untertitel ?? null,
      kennung,
      status: felder.status ?? null,
      werte,
      bezuege: bz,
    },
  };
}

const fr = (n: number): string =>
  `Fr. ${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Durchstich 1: Aus den Schadstellen einer Aufnahme entsteht je Maßnahmenart
 * eine Kalkulation – Menge, Material, Personal, Fahrzeug, Dauer und Preis
 * aus den hinterlegten Ansätzen. Eine bestehende Kalkulation derselben
 * Maßnahmenart wird ersetzt (neue Version, alte „Überholt“).
 */
export function kalkulationAusAufnahme(
  b: Datenbestand,
  aufnahmeId: string
): Aenderung {
  const aufnahme = satz(b, "baustellenaufnahme", aufnahmeId);
  if (!aufnahme) return fehlerMeldung("Aufnahme nicht gefunden.");
  const stellen = mit(
    b,
    "schadstelle",
    "schadstelle-gehoert-zu-baustellenaufnahme",
    aufnahmeId
  );
  if (stellen.length === 0)
    return fehlerMeldung(
      "Erst Schadstellen erfassen – ohne Mengen gibt es nichts zu rechnen."
    );
  const ohneMassnahme = stellen.filter(
    (s) => !ein(s.bezuege["schadstelle-erfordert-massnahmenart"])
  );
  if (ohneMassnahme.length === stellen.length) {
    return fehlerMeldung(
      "Keiner Schadstelle ist eine Maßnahmenart zugeordnet."
    );
  }

  const gruppen = new Map<string, Datensatz[]>();
  for (const s of stellen) {
    const ma = ein(s.bezuege["schadstelle-erfordert-massnahmenart"]);
    if (ma) gruppen.set(ma, [...(gruppen.get(ma) ?? []), s]);
  }

  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const zeilen: string[] = [];
  for (const [maId, gruppe] of gruppen) {
    const ma = satz(b, "massnahmenart", maId);
    if (!ma) continue;
    const menge = runde(
      gruppe.reduce((sum, s) => sum + zahl(s.werte.schadensmenge), 0),
      1
    );
    const einheit = String(ma.werte.abrechnungseinheit ?? "");
    const bm = zahl(ma.werte.bindemittelansatz);
    const sp = zahl(ma.werte.splittansatz);
    const leistung = zahl(ma.werte.leistungJeStunde) || 1;
    const team = zahl(ma.werte.teamgroesse) || 2;
    const dauer = Math.max(0.5, runde(menge / leistung, 1));
    const preis = runde(menge * zahl(ma.werte.einheitspreis));

    const alt = mit(
      b,
      "kalkulation",
      "kalkulation-basiert-auf-baustellenaufnahme",
      aufnahmeId
    ).filter(
      (k) =>
        ein(k.bezuege["kalkulation-fuer-massnahmenart"]) === maId &&
        k.status !== "Überholt"
    );
    const version =
      alt.reduce((v, k) => Math.max(v, zahl(k.werte.version)), 0) + 1;
    for (const k of alt)
      out.geaendert.push({ slug: "kalkulation", id: k.id, status: "Überholt" });

    const kalk = neu(
      b,
      "kalkulation",
      out.neu,
      {
        titel: `${ma.titel} · Version ${version}`,
        untertitel: `${menge} ${einheit} · ${fr(preis)}`,
        status: "Entwurf",
        werte: {
          kalkulationsdatum: heute(),
          version,
          kalkulierteFlaeche: menge,
          bindemittelansatz: bm,
          splittansatz: sp,
          anzahlMitarbeitende: team,
          benoetigteFahrzeugarten:
            bm > 0 ? ["Spezialfahrzeug", "Lieferwagen"] : ["Lieferwagen"],
          ausfuehrungsdauer: dauer,
          kalkulierterPreis: preis,
          annahmen: `Automatisch aus ${gruppe.length} Schadstelle(n) gerechnet. Ansätze sind Beispielwerte.`,
        },
      },
      {
        "kalkulation-basiert-auf-baustellenaufnahme": aufnahmeId,
        "kalkulation-fuer-verkaufschance": ein(
          aufnahme.bezuege["baustellenaufnahme-fuer-verkaufschance"]
        ),
        "kalkulation-fuer-massnahmenart": maId,
        "kalkulation-ersetzt-kalkulation": alt[0]?.id,
      }
    );
    out.neu.push(kalk);

    const artikelId = gruppe.flatMap((s) =>
      liste(s.bezuege["schadstelle-empfiehlt-artikel"])
    )[0];
    const artikel = satz(b, "artikel", artikelId);
    const kg = bm > 0 ? runde(menge * bm, 1) : runde(menge * 30, 1);
    const kgPreis = artikel
      ? runde(
          zahl(artikel.werte.einstandspreis) /
            (zahl(artikel.werte.gebindegroesse) || 1),
          2
        )
      : 2.5;
    const positionen: [
      string,
      string,
      number,
      string,
      number,
      string | undefined,
    ][] = [
      [
        "Material",
        artikel ? String(artikel.werte.bezeichnung) : "Bindemittel",
        kg,
        "kg",
        kgPreis,
        artikel?.id,
      ],
      ...(sp > 0
        ? [
            [
              "Material",
              "Edelsplitt 4/8",
              runde(menge * sp, 1),
              "kg",
              0.1,
              "ar5",
            ] as [string, string, number, string, number, string],
          ]
        : []),
      [
        "Personal",
        `Team (${team} Personen)`,
        runde(dauer * team, 1),
        "h",
        80,
        undefined,
      ],
      [
        "Fahrzeug",
        bm > 0 ? "Spritzfahrzeug Rissverguss" : "Lieferwagen",
        dauer,
        "h",
        bm > 0 ? 145 : 45,
        undefined,
      ],
    ];
    for (const [art, bez, m, eh, satzKosten, art_id] of positionen) {
      out.neu.push(
        neu(
          b,
          "kalkulationsposition",
          out.neu,
          {
            titel: bez,
            untertitel: `${art} · ${m} ${eh}`,
            werte: {
              positionsart: art,
              bezeichnung: bez,
              menge: m,
              einheit: eh,
              kostensatz: satzKosten,
            },
          },
          {
            "kalkulationsposition-gehoert-zu-kalkulation": kalk.satz.id,
            "kalkulationsposition-verwendet-artikel": art_id,
          }
        )
      );
    }
    zeilen.push(`${ma.titel}: ${menge} ${einheit}, ${dauer} h, ${fr(preis)}`);
  }
  out.meldung = `Kalkulation gerechnet – ${zeilen.join(" · ")}`;
  if (ohneMassnahme.length > 0)
    out.meldung += ` (${ohneMassnahme.length} Schadstelle(n) ohne Maßnahmenart nicht berücksichtigt)`;
  return out;
}

/** Durchstich 1: Aus den freigegebenen Kalkulationen einer Aufnahme entsteht das Angebot. */
export function angebotAusAufnahme(
  b: Datenbestand,
  aufnahmeId: string,
  personaId: string | null
): Aenderung {
  const aufnahme = satz(b, "baustellenaufnahme", aufnahmeId);
  if (!aufnahme) return fehlerMeldung("Aufnahme nicht gefunden.");
  const kalks = mit(
    b,
    "kalkulation",
    "kalkulation-basiert-auf-baustellenaufnahme",
    aufnahmeId
  ).filter((k) => k.status === "Freigegeben");
  if (kalks.length === 0)
    return fehlerMeldung(
      "Keine freigegebene Kalkulation – zuerst rechnen und freigeben."
    );
  const soId = ein(
    aufnahme.bezuege["baustellenaufnahme-betrifft-strassenobjekt"]
  );
  const so = satz(b, "strassenobjekt", soId);
  const kundeId = so
    ? ein(so.bezuege["strassenobjekt-gehoert-zu-kunde"])
    : undefined;
  const kunde = satz(b, "kunde", kundeId);
  if (!kunde)
    return fehlerMeldung("Zum Straßenobjekt ist kein Kunde hinterlegt.");
  const ansprech = mit(
    b,
    "ansprechpartner",
    "ansprechpartner-gehoert-zu-kunde",
    kunde.id
  )[0];
  const datum = heute();
  const gueltig = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const angebot = neu(
    b,
    "angebot",
    out.neu,
    {
      titel:
        `${kalks.map((k) => k.titel.split(" · ")[0]).join(" und ")} ${so?.titel ?? ""}`.trim(),
      untertitel: `${kunde.titel} · ${datum}`,
      status: "In Erstellung",
      kennungFeld: "angebotsnummer",
      werte: {
        dokumentart: "Angebot",
        angebotsdatum: datum,
        gueltigBis: gueltig,
        version: 1,
        leistungsbeschreibung: `Leistungen gemäss Aufnahme vom ${String(aufnahme.werte.aufnahmedatum ?? datum)}, inkl. Signalisation.`,
        kalkulationsannahmen:
          "Mengen aus der Baustellenaufnahme, Ansätze laut Maßnahmenkatalog (Beispielwerte).",
      },
    },
    {
      "angebot-an-kunde": kunde.id,
      "angebot-an-ansprechpartner": ansprech?.id,
      "angebot-erstellt-von-mitarbeiter": personaId ?? undefined,
      "angebot-zu-verkaufschance": ein(
        aufnahme.bezuege["baustellenaufnahme-fuer-verkaufschance"]
      ),
      "angebot-basiert-auf-kalkulation": kalks.map((k) => k.id),
      "angebot-betrifft-strassenobjekt": soId ? [soId] : [],
    }
  );
  out.neu.push(angebot);

  let nr = 1;
  let summe = 0;
  for (const k of kalks) {
    const maId = ein(k.bezuege["kalkulation-fuer-massnahmenart"]);
    const ma = satz(b, "massnahmenart", maId);
    const menge = zahl(k.werte.kalkulierteFlaeche);
    const preis = zahl(ma?.werte.einheitspreis);
    summe += menge * preis;
    out.neu.push(
      neu(
        b,
        "angebotsposition",
        out.neu,
        {
          titel: ma?.titel ?? k.titel,
          untertitel: `${menge} ${String(ma?.werte.abrechnungseinheit ?? "")} à ${fr(preis)}`,
          werte: {
            positionsnummer: nr++,
            bezeichnung: ma?.titel ?? k.titel,
            menge,
            einheit: String(ma?.werte.abrechnungseinheit ?? "Stk"),
            einzelpreis: preis,
          },
        },
        {
          "angebotsposition-gehoert-zu-angebot": angebot.satz.id,
          "angebotsposition-bietet-an-massnahmenart": maId,
        }
      )
    );
  }
  out.neu.push(
    neu(
      b,
      "angebotsposition",
      out.neu,
      {
        titel: "Signalisation und Verkehrsdienst",
        untertitel: `1 pauschal à ${fr(380)}`,
        werte: {
          positionsnummer: nr,
          bezeichnung: "Signalisation und Verkehrsdienst",
          menge: 1,
          einheit: "pauschal",
          einzelpreis: 380,
        },
      },
      { "angebotsposition-gehoert-zu-angebot": angebot.satz.id }
    )
  );
  out.meldung = `Angebot ${angebot.satz.kennung ?? ""} erstellt – ${nr} Positionen, ${fr(runde(summe + 380))} netto.`;
  const entwuerfe = mit(
    b,
    "kalkulation",
    "kalkulation-basiert-auf-baustellenaufnahme",
    aufnahmeId
  ).filter((k) => k.status === "Entwurf");
  if (entwuerfe.length > 0)
    out.meldung += ` ${entwuerfe.length} Kalkulation(en) im Entwurf nicht übernommen.`;
  out.oeffnen = { slug: "angebot", id: angebot.satz.id };
  return out;
}

/** Aus dem angenommenen Angebot wird der Auftrag mit denselben Positionen. */
export function auftragAusAngebot(
  b: Datenbestand,
  angebotId: string
): Aenderung {
  const an = satz(b, "angebot", angebotId);
  if (!an) return fehlerMeldung("Angebot nicht gefunden.");
  const positionen = mit(
    b,
    "angebotsposition",
    "angebotsposition-gehoert-zu-angebot",
    angebotId
  );
  const handel = positionen.some(
    (p) => p.bezuege["angebotsposition-bietet-an-artikel"]
  );
  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const auftrag = neu(
    b,
    "auftrag",
    out.neu,
    {
      titel: an.titel,
      untertitel: `${an.untertitel?.split(" · ")[0] ?? ""} · ${handel ? "Handel" : "Dienstleistung"}`,
      status: "Erteilt",
      kennungFeld: "auftragsnummer",
      werte: {
        auftragsart: handel ? "Handel" : "Dienstleistung",
        auftragsdatum: heute(),
      },
    },
    {
      "auftrag-von-kunde": ein(an.bezuege["angebot-an-kunde"]),
      "auftrag-ansprechpartner": ein(an.bezuege["angebot-an-ansprechpartner"]),
      "auftrag-betrifft-strassenobjekt": liste(
        an.bezuege["angebot-betrifft-strassenobjekt"]
      ),
      "auftrag-aus-angebot": angebotId,
      "auftrag-aus-bestellung": ein(an.bezuege["angebot-zu-bestellung"]),
    }
  );
  out.neu.push(auftrag);
  for (const p of positionen) {
    out.neu.push(
      neu(
        b,
        "auftragsposition",
        out.neu,
        {
          titel: p.titel,
          untertitel: p.untertitel ?? undefined,
          werte: { ...p.werte, zusatzleistung: false },
        },
        {
          "auftragsposition-gehoert-zu-auftrag": auftrag.satz.id,
          "auftragsposition-umfasst-artikel": ein(
            p.bezuege["angebotsposition-bietet-an-artikel"]
          ),
          "auftragsposition-umfasst-massnahmenart": ein(
            p.bezuege["angebotsposition-bietet-an-massnahmenart"]
          ),
          "auftragsposition-uebernimmt-angebotsposition": p.id,
        }
      )
    );
  }
  out.meldung = `Auftrag ${auftrag.satz.kennung ?? ""} aus dem Angebot angelegt – ${positionen.length} Positionen übernommen.`;
  out.oeffnen = { slug: "auftrag", id: auftrag.satz.id };
  return out;
}

/** Händler-Nachbestellung ohne Auftragsbestätigung: direkt zum Auftrag (Relation „Auftrag aus Bestellung“). */
export function auftragAusBestellung(
  b: Datenbestand,
  bestellungId: string
): Aenderung {
  const bs = satz(b, "bestellung", bestellungId);
  if (!bs) return fehlerMeldung("Bestellung nicht gefunden.");
  const kundeId = ein(bs.bezuege["bestellung-von-kunde"]);
  if (!kundeId) return fehlerMeldung("Erst den Kunden zuordnen.");
  const artikel = liste(bs.bezuege["bestellung-bestellt-artikel"])
    .map((id) => satz(b, "artikel", id))
    .filter((a): a is Datensatz => a != null);
  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const kunde = satz(b, "kunde", kundeId);
  const auftrag = neu(
    b,
    "auftrag",
    out.neu,
    {
      titel: `Baustofflieferung ${kunde?.titel ?? ""}`.trim(),
      untertitel: `${kunde?.titel ?? ""} · Handel`,
      status: "Erteilt",
      kennungFeld: "auftragsnummer",
      werte: {
        auftragsart: "Handel",
        auftragsdatum: heute(),
        kundenreferenz: String(bs.werte.kundenreferenz ?? ""),
        liefertermin: String(bs.werte.gewuenschterLiefertermin ?? heute()),
        bemerkungen: `Aus Bestellung: ${String(bs.werte.bestellinhalt ?? "")}`,
      },
    },
    {
      "auftrag-von-kunde": kundeId,
      "auftrag-ansprechpartner": ein(
        bs.bezuege["bestellung-bestellt-durch-ansprechpartner"]
      ),
      "auftrag-aus-bestellung": bestellungId,
    }
  );
  out.neu.push(auftrag);
  artikel.forEach((a, i) =>
    out.neu.push(
      neu(
        b,
        "auftragsposition",
        out.neu,
        {
          titel: `${String(a.werte.bezeichnung)}, ${String(a.werte.gebindeeinheit)}`,
          untertitel: "Menge aus der Bestellung prüfen",
          werte: {
            positionsnummer: i + 1,
            bezeichnung: String(a.werte.bezeichnung),
            menge: 1,
            einheit: "Stk",
            einzelpreis: zahl(a.werte.verkaufspreis),
            zusatzleistung: false,
          },
        },
        {
          "auftragsposition-gehoert-zu-auftrag": auftrag.satz.id,
          "auftragsposition-umfasst-artikel": a.id,
        }
      )
    )
  );
  out.meldung = `Auftrag ${auftrag.satz.kennung ?? ""} direkt aus der Bestellung angelegt – Mengen bitte prüfen.`;
  out.oeffnen = { slug: "auftrag", id: auftrag.satz.id };
  return out;
}

/** Ausmass aus den Rapporten: ausgeführte Mengen je Auftragsposition zur Kundenfreigabe. */
export function ausmassAusAuftrag(
  b: Datenbestand,
  auftragId: string
): Aenderung {
  const au = satz(b, "auftrag", auftragId);
  if (!au) return fehlerMeldung("Auftrag nicht gefunden.");
  const positionen = mit(
    b,
    "auftragsposition",
    "auftragsposition-gehoert-zu-auftrag",
    auftragId
  );
  const einsaetze = mit(b, "einsatz", "einsatz-fuer-auftrag", auftragId).map(
    (e) => e.id
  );
  const gemeldet = saetze(b, "rapportzeile")
    .filter(
      (z) =>
        einsaetze.includes(ein(z.bezuege["rapportzeile-zu-einsatz"]) ?? "") &&
        z.werte.zeilenart === "Ausgeführte Menge"
    )
    .reduce((s, z) => s + zahl(z.werte.gemeldeteMenge), 0);
  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const prot = neu(
    b,
    "ausmassprotokoll",
    out.neu,
    {
      titel: `Ausmaß ${au.titel}`,
      untertitel: heute(),
      status: "Erstellt",
      kennungFeld: "protokollnummer",
      werte: { ausmassdatum: heute() },
    },
    { "ausmassprotokoll-zu-auftrag": auftragId }
  );
  out.neu.push(prot);
  let ersteMassnahme = true;
  for (const p of positionen) {
    const istMassnahme = Boolean(
      p.bezuege["auftragsposition-umfasst-massnahmenart"]
    );
    const menge =
      istMassnahme && ersteMassnahme && gemeldet > 0
        ? gemeldet
        : zahl(p.werte.menge);
    if (istMassnahme) ersteMassnahme = false;
    out.neu.push(
      neu(
        b,
        "ausmassposition",
        out.neu,
        {
          titel: p.titel,
          untertitel: `${menge} ${String(p.werte.einheit ?? "")}`,
          werte: {
            bezeichnung: p.titel,
            ausgefuehrteMenge: menge,
            einheit: String(p.werte.einheit ?? ""),
            nachweis:
              istMassnahme && gemeldet > 0
                ? "Summe der Rapportzeilen"
                : "laut Auftrag",
          },
        },
        {
          "ausmassposition-gehoert-zu-ausmassprotokoll": prot.satz.id,
          "ausmassposition-misst-auftragsposition": p.id,
        }
      )
    );
  }
  out.meldung = `Ausmaß ${prot.satz.kennung ?? ""} erstellt – ${positionen.length} Positionen, bereit zum Übermitteln.`;
  out.oeffnen = { slug: "ausmassprotokoll", id: prot.satz.id };
  return out;
}

/** Rechnung in Bexio erstellen: aus dem freigegebenen Ausmass oder (Handel) aus den Auftragspositionen. */
export function rechnungAusAuftrag(
  b: Datenbestand,
  auftragId: string
): Aenderung {
  const au = satz(b, "auftrag", auftragId);
  if (!au) return fehlerMeldung("Auftrag nicht gefunden.");
  const positionen = mit(
    b,
    "auftragsposition",
    "auftragsposition-gehoert-zu-auftrag",
    auftragId
  );
  const ausmass = mit(
    b,
    "ausmassprotokoll",
    "ausmassprotokoll-zu-auftrag",
    auftragId
  ).find((a) => a.status === "Freigegeben");
  if (au.werte.auftragsart === "Dienstleistung" && !ausmass) {
    return fehlerMeldung(
      "Für Dienstleistungen braucht es ein vom Kunden freigegebenes Ausmaß."
    );
  }
  let netto = 0;
  if (ausmass) {
    for (const ap of mit(
      b,
      "ausmassposition",
      "ausmassposition-gehoert-zu-ausmassprotokoll",
      ausmass.id
    )) {
      const pos = satz(
        b,
        "auftragsposition",
        ein(ap.bezuege["ausmassposition-misst-auftragsposition"])
      );
      netto += zahl(ap.werte.ausgefuehrteMenge) * zahl(pos?.werte.einzelpreis);
    }
  } else {
    netto = positionen.reduce(
      (s, p) => s + zahl(p.werte.menge) * zahl(p.werte.einzelpreis),
      0
    );
  }
  netto = runde(netto);
  const brutto = auf5Rappen(netto * 1.081);
  const kundeId = ein(au.bezuege["auftrag-von-kunde"]);
  const kunde = satz(b, "kunde", kundeId);
  const faellig = new Date(
    Date.now() + zahl(kunde?.werte.zahlungsziel ?? 30) * 864e5
  )
    .toISOString()
    .slice(0, 10);
  const bexio = String(1100 + saetze(b, "rechnung").length * 7);
  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const re = neu(
    b,
    "rechnung",
    out.neu,
    {
      titel: "Rechnung",
      untertitel: `${kunde?.titel ?? ""} · ${fr(brutto)}`,
      status: "Erstellt",
      kennungFeld: "rechnungsnummer",
      werte: {
        bexioRechnungsnummer: bexio,
        rechnungsdatum: heute(),
        faelligkeitsdatum: faellig,
        betragNetto: netto,
        betragBrutto: brutto,
        mahnstufe: 0,
      },
    },
    { "rechnung-zu-auftrag": auftragId, "rechnung-an-kunde": kundeId }
  );
  re.satz.titel = `Rechnung ${re.satz.kennung ?? ""}`;
  out.neu.push(re);
  out.meldung = `Rechnung ${re.satz.kennung ?? ""} in Bexio angelegt (simuliert, Bexio-Nr. ${bexio}) – ${fr(brutto)} inkl. 8.1 % MWST.`;
  out.oeffnen = { slug: "rechnung", id: re.satz.id };
  return out;
}

export function nachkalkulationAusAuftrag(
  b: Datenbestand,
  auftragId: string
): Aenderung {
  const au = satz(b, "auftrag", auftragId);
  if (!au) return fehlerMeldung("Auftrag nicht gefunden.");
  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const nk = neu(
    b,
    "nachkalkulation",
    out.neu,
    {
      titel: `Nachkalkulation ${au.titel}`,
      untertitel: `Stichtag ${heute()}`,
      status: "In Erstellung",
      werte: { stichtag: heute() },
    },
    { "nachkalkulation-zu-auftrag": auftragId }
  );
  out.neu.push(nk);
  out.meldung =
    "Nachkalkulation angelegt – Kosten aus Rapporten und Material sind zugeordnet.";
  out.oeffnen = { slug: "nachkalkulation", id: nk.satz.id };
  return out;
}

export function rapportAusEinsatz(
  b: Datenbestand,
  einsatzId: string
): Aenderung {
  const e = satz(b, "einsatz", einsatzId);
  if (!e) return fehlerMeldung("Einsatz nicht gefunden.");
  const out: Aenderung = { neu: [], geaendert: [], meldung: "" };
  const rp = neu(
    b,
    "arbeitsrapport",
    out.neu,
    {
      titel: "Rapport",
      untertitel: String(e.werte.einsatzdatum ?? heute()),
      status: "In Erfassung",
      kennungFeld: "rapportnummer",
      werte: { rapportdatum: String(e.werte.einsatzdatum ?? heute()) },
    },
    { "arbeitsrapport-zu-einsatz": einsatzId }
  );
  rp.satz.titel = `Rapport ${rp.satz.kennung ?? ""}`;
  out.neu.push(rp);
  out.meldung = `Rapport ${rp.satz.kennung ?? ""} angelegt – Zeiten und Mengen als Rapportzeilen erfassen.`;
  out.oeffnen = { slug: "arbeitsrapport", id: rp.satz.id };
  return out;
}

/** Prüfung vor „Ausführung freigeben“: Prüfwerte vorhanden, Boden warm genug, Oberfläche trocken. */
export function pruefeAusfuehrung(
  b: Datenbestand,
  einsatzId: string
): string | null {
  const e = satz(b, "einsatz", einsatzId);
  if (!e) return "Einsatz nicht gefunden.";
  if (e.werte.bodentemperatur == null)
    return "Erst Prüfwerte erfassen (Boden- und Lufttemperatur, Oberfläche).";
  if (e.werte.oberflaecheTrocken === false)
    return "Oberfläche nicht trocken – Ausführung nicht freigeben.";
  const auftragId = ein(e.bezuege["einsatz-fuer-auftrag"]);
  const minimum = mit(
    b,
    "auftragsposition",
    "auftragsposition-gehoert-zu-auftrag",
    auftragId ?? ""
  )
    .map((p) =>
      satz(
        b,
        "massnahmenart",
        ein(p.bezuege["auftragsposition-umfasst-massnahmenart"])
      )
    )
    .filter((m): m is Datensatz => m != null)
    .reduce(
      (max, m) => Math.max(max, zahl(m.werte.mindesttemperaturBoden)),
      -99
    );
  if (minimum > -99 && zahl(e.werte.bodentemperatur) < minimum) {
    return `Bodentemperatur ${zahl(e.werte.bodentemperatur)} °C liegt unter dem Minimum von ${minimum} °C der Maßnahme.`;
  }
  return null;
}

export function zahlungseingang(
  b: Datenbestand,
  rechnungId: string
): Aenderung {
  const re = satz(b, "rechnung", rechnungId);
  if (!re) return fehlerMeldung("Rechnung nicht gefunden.");
  return {
    neu: [],
    geaendert: [
      {
        slug: "rechnung",
        id: rechnungId,
        status: "Bezahlt",
        werte: {
          bezahlterBetrag: zahl(re.werte.betragBrutto),
          bezahltAm: heute(),
        },
      },
    ],
    meldung: `Zahlungseingang aus Bexio übernommen (simuliert) – ${fr(zahl(re.werte.betragBrutto))}.`,
  };
}

export function transportBeauftragen(
  b: Datenbestand,
  lieferungId: string
): Aenderung {
  const li = satz(b, "lieferung", lieferungId);
  if (!li) return fehlerMeldung("Lieferung nicht gefunden.");
  const nummer = `GAL${Date.now().toString().slice(-8)}`;
  return {
    neu: [],
    geaendert: [
      {
        slug: "lieferung",
        id: lieferungId,
        werte: {
          transportdienstleister: String(
            li.werte.transportdienstleister ?? "Galliker Transport"
          ),
          sendungsnummer: nummer,
        },
      },
    ],
    meldung: `Transport beauftragt (simuliert) – Sendungsnummer ${nummer}.`,
  };
}

export { jetzt };
